// Olivium Governance — command palette + global search index (pure, testable).
//
// One index powers the command palette, global search, the create menu, and the
// test-as-role switcher. Everything is derived from the canonical state so there
// are no stale or fabricated entries.

import { GOV_MODULES, GOV_MODULE_DEFS, OLV_SUITE_ROOT, govModulePath, govRecordPath, type GovModule } from './governanceCards'
import { GOV_ROLES, type GovState, type GovRole, recordsByModule } from './governanceModel'

export type CommandKind = 'navigate' | 'create' | 'role' | 'record' | 'reset'

export interface CommandItem {
  id: string
  label: string
  hint: string
  kind: CommandKind
  path?: string
  module?: GovModule
  role?: GovRole | null
}

/** Navigate-to commands: the suite home + each governance module. */
export function navigateCommands(): CommandItem[] {
  const items: CommandItem[] = [
    { id: 'nav:home', label: 'Go to Olivium home', hint: 'Suite', kind: 'navigate', path: OLV_SUITE_ROOT },
  ]
  for (const m of GOV_MODULES) {
    const d = GOV_MODULE_DEFS[m]
    items.push({ id: `nav:${m}`, label: `Go to ${d.title}`, hint: 'Module', kind: 'navigate', path: govModulePath(m), module: m })
  }
  return items
}

/** Create commands: open the create flow in each governance module. */
export function createCommands(): CommandItem[] {
  return GOV_MODULES.map((m) => {
    const d = GOV_MODULE_DEFS[m]
    return { id: `create:${m}`, label: `Create ${d.recordNoun} in ${d.title}`, hint: 'Create', kind: 'create' as const, path: govModulePath(m), module: m }
  })
}

/** Test-as-role commands: preview the suite as each role, plus reset. */
export function roleCommands(): CommandItem[] {
  const items: CommandItem[] = GOV_ROLES.map((r) => ({
    id: `role:${r}`, label: `Test as ${r}`, hint: 'Role', kind: 'role' as const, role: r,
  }))
  items.push({ id: 'role:reset', label: 'Reset to actual role', hint: 'Role', kind: 'role', role: null })
  return items
}

/** Record search across every module. */
export function recordResults(state: GovState, query: string, limit = 8): CommandItem[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  const out: CommandItem[] = []
  for (const m of GOV_MODULES) {
    for (const r of recordsByModule(state, m)) {
      const hay = `${r.title} ${r.summary} ${r.id} ${Object.values(r.fields).join(' ')}`.toLowerCase()
      if (hay.includes(q)) {
        out.push({
          id: `record:${r.id}`, label: r.title, hint: `${GOV_MODULE_DEFS[m].title} · ${r.status}`,
          kind: 'record', path: govRecordPath(m, r.id), module: m,
        })
      }
    }
  }
  return out.slice(0, limit)
}

/** The full ranked result set for a query. Empty query returns the static commands. */
export function searchCommands(state: GovState, query: string): CommandItem[] {
  const q = query.trim().toLowerCase()
  const statics = [...navigateCommands(), ...createCommands(), ...roleCommands()]
  if (!q) return statics
  const matchedStatics = statics.filter((c) => c.label.toLowerCase().includes(q) || c.hint.toLowerCase().includes(q))
  return [...recordResults(state, query), ...matchedStatics]
}
